import * as React from "react";
import { CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, Search, Store } from "lucide-react";
import { FormattedNumber } from "react-intl";
import { Link } from "react-router-dom";
import type { Reseller as ApiReseller } from "randmar-api-client";
import {
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
} from "randmarcomps";
import CurrencyUtils from "../util/CurrencyUtils";
import Path from "../../routes/Path";
import SalesSummary from "../../componentsCore/cards/SalesSummary";
import ResellerActions from "../reseller/ResellerActions";
import { formatRandmarDate } from "../../utils/dates";

interface ResellersGridProps {
  resellers?: ApiReseller[];
  applicationId: string;
  fiscalYearMonthStart?: number;
  defaultRowsPerPage?: number;
}

const toLocationLabel = (reseller: ApiReseller) =>
  [reseller.City, reseller.Province, reseller.Country].filter(Boolean).join(", ");

const getPriorityBadge = (
  priority?: string | null
): { label: string; className: string } | null => {
  if (!priority) {
    return null;
  }

  const normalized = priority.toLowerCase();
  if (normalized.includes("exclusive")) {
    return { label: priority, className: "border-emerald-200 bg-emerald-50 text-emerald-700" };
  }
  if (normalized.includes("commercial")) {
    return { label: priority, className: "border-amber-200 bg-amber-50 text-amber-800" };
  }
  if (normalized.includes("e-commerce") || normalized.includes("ecommerce")) {
    return { label: priority, className: "border-sky-200 bg-sky-50 text-sky-700" };
  }
  if (normalized.includes("hold")) {
    return { label: priority, className: "border-rose-200 bg-rose-50 text-rose-700" };
  }

  return { label: priority, className: "border-slate-200 bg-slate-50 text-slate-700" };
};

const rowsPerPageOptions = [10, 25, 50, 100] as const;

const formatInvoiceDate = (value?: number | null) => {
  return formatRandmarDate(value) ?? "—";
};

type StatusFilter = "all" | "active" | "on-hold";

const isQualifiedReseller = (reseller: ApiReseller) => {
  const rawIsQualified = (reseller as unknown as { IsQualified?: boolean | null }).IsQualified;
  return rawIsQualified === true;
};

const ResellersGrid: React.FC<ResellersGridProps> = ({
  resellers,
  applicationId,
  fiscalYearMonthStart,
  defaultRowsPerPage = 25,
}) => {
  const safeResellers = React.useMemo(() => resellers ?? [], [resellers]);
  const [searchValue, setSearchValue] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>("all");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(defaultRowsPerPage);
  const [selectedResellerForSales, setSelectedResellerForSales] = React.useState<ApiReseller | null>(null);

  const normalizedSearchValue = searchValue.trim().toLowerCase();
  const showSearch = safeResellers.length > 3;

  const statusCounts = React.useMemo(() => {
    const total = safeResellers.length;
    const onHold = safeResellers.filter((row) => row.OnHold === 1).length;
    const active = total - onHold;
    return { total, active, onHold };
  }, [safeResellers]);

  const filteredResellers = React.useMemo(() => {
    const byStatus = safeResellers.filter((row) => {
      const isOnHold = row.OnHold === 1;
      if (statusFilter === "active") return !isOnHold;
      if (statusFilter === "on-hold") return isOnHold;
      return true;
    });

    if (!normalizedSearchValue) return byStatus;

    return byStatus.filter((row) => {
      const salesLast30Days = row.SalesData?.SalesLast30Days ?? 0;
      const salesLast3Months = row.SalesData?.SalesLast3Months ?? 0;
      const haystack = [
        row.ResellerId,
        row.PublicName,
        row.Name,
        toLocationLabel(row),
        row.Priority,
        row.OnHold === 1 ? "on hold" : "active",
        salesLast30Days.toString(),
        salesLast3Months.toString(),
        formatInvoiceDate(row.LastInvoiceDate),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedSearchValue);
    });
  }, [normalizedSearchValue, safeResellers, statusFilter]);

  const totalRows = filteredResellers.length;
  const safeRowsPerPage = Math.max(rowsPerPage, 1);
  const totalPages = Math.max(1, Math.ceil(totalRows / safeRowsPerPage));
  const safePage = Math.min(page, totalPages - 1);
  const startRow = safePage * safeRowsPerPage;
  const endRow = Math.min(totalRows, startRow + safeRowsPerPage);

  React.useEffect(() => {
    setPage(0);
  }, [rowsPerPage]);

  React.useEffect(() => {
    setPage(0);
  }, [normalizedSearchValue, statusFilter]);

  React.useEffect(() => {
    setPage((currentPage) => Math.min(currentPage, totalPages - 1));
  }, [totalPages]);

  const pagedResellers = React.useMemo(
    () => filteredResellers.slice(startRow, endRow),
    [endRow, filteredResellers, startRow]
  );

  const startIndex = totalRows === 0 ? 0 : startRow + 1;
  const endIndex = endRow;

  if (safeResellers.length === 0) {
    return (
      <Card className="w-full rounded-2xl border border-gray-100 bg-white p-6 text-center text-slate-500 shadow-sm">
        <div className="mx-auto flex max-w-md flex-col items-center gap-2">
          <Store className="h-10 w-10 text-slate-300" aria-hidden="true" />
          <p className="text-[15px] font-medium text-slate-900">No resellers yet</p>
          <p className="text-[13px] text-slate-500">Qualified resellers will appear here once they are available for your account.</p>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card className="w-full overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-gray-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            {showSearch ? (
              <div className="relative w-full max-w-sm">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                <Input
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="Search resellers…"
                  className="h-11 rounded-lg pl-10"
                  aria-label="Search resellers"
                />
              </div>
            ) : null}

            <Tabs
              value={statusFilter}
              onValueChange={(value) => {
                if (value === "all" || value === "active" || value === "on-hold") {
                  setStatusFilter(value);
                }
              }}
              className="w-full sm:w-auto"
            >
              <div className="overflow-x-auto rounded-xl bg-slate-50 p-1">
                <TabsList>
                  <TabsTrigger value="all">All ({statusCounts.total})</TabsTrigger>
                  <TabsTrigger value="active">Active ({statusCounts.active})</TabsTrigger>
                  <TabsTrigger value="on-hold" disabled={statusCounts.onHold === 0}>
                    On hold ({statusCounts.onHold})
                  </TabsTrigger>
                </TabsList>
              </div>
            </Tabs>
          </div>

          <p className="text-[13px] text-slate-500">
            {normalizedSearchValue || statusFilter !== "all"
              ? `${totalRows} result${totalRows === 1 ? "" : "s"} (from ${statusCounts.total})`
              : `${statusCounts.total} reseller${statusCounts.total === 1 ? "" : "s"}`}
          </p>
        </div>

        {filteredResellers.length === 0 ? (
          <div className="grid place-items-center p-10 text-center">
            <p className="text-[15px] font-medium text-slate-900">No matching resellers</p>
            <p className="mt-1 text-[13px] text-slate-500">Try a different search or clear filters.</p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <Button type="button" variant="outline" className="rounded-xl" onClick={() => setSearchValue("")} disabled={!searchValue}>
                Clear search
              </Button>
              <Button
                type="button"
                variant="outline"
                className="rounded-xl"
                onClick={() => setStatusFilter("all")}
                disabled={statusFilter === "all"}
              >
                Clear filter
              </Button>
            </div>
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow className="h-12">
                  <TableHead className="w-16 whitespace-nowrap text-center text-[12px] font-semibold uppercase tracking-wide text-slate-500" />
                  <TableHead className="min-w-[220px] whitespace-nowrap text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                    Name
                  </TableHead>
                  <TableHead className="min-w-[200px] whitespace-nowrap text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                    Location
                  </TableHead>
                  <TableHead className="min-w-[160px] whitespace-nowrap text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                    Package
                  </TableHead>
                  <TableHead className="whitespace-nowrap text-center text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                    Last invoice
                  </TableHead>
                  <TableHead className="whitespace-nowrap text-right text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                    Sales (30 days)
                  </TableHead>
                  <TableHead className="whitespace-nowrap text-right text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                    Sales last 3 months
                  </TableHead>
                  <TableHead className="w-24 whitespace-nowrap text-right text-[12px] font-semibold uppercase tracking-wide text-slate-500" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedResellers.map((reseller, index) => {
                  const badge = getPriorityBadge(reseller.Priority);
                  const resellerId = reseller.ResellerId ?? "";
                  const resellerName = reseller.PublicName || reseller.Name || "Reseller";
                  const salesLast30Days = reseller.SalesData?.SalesLast30Days ?? 0;
                  const salesLast3Months = reseller.SalesData?.SalesLast3Months ?? 0;
                  const onHold = reseller.OnHold === 1;
                  const isQualified = isQualifiedReseller(reseller);

                  return (
                    <TableRow
                      key={resellerId || `${resellerName}-${index}`}
                      className="h-14 transition-colors hover:bg-slate-50"
                    >
                      <TableCell className="text-center">
                        {resellerId ? (
                          <img
                            src={`https://api.randmar.io/V4/Partner/${resellerId}/Account/Logo?width=75`}
                            alt={`${resellerName} logo`}
                            loading="lazy"
                            className="mx-auto h-10 w-10 rounded-xl border border-slate-100 bg-white object-contain"
                          />
                        ) : (
                          <div className="mx-auto grid h-10 w-10 place-items-center rounded-xl border border-slate-100 bg-slate-50">
                            <Store className="h-5 w-5 text-slate-400" aria-hidden="true" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-[15px]">
                        <div className="flex flex-col gap-1">
                          <Link
                            to={Path.reseller(applicationId, resellerId)}
                            className="inline-flex items-center gap-1 font-medium text-slate-900 hover:text-[color:var(--ops-accent)] hover:underline"
                          >
                            <span>{resellerName}</span>
                            {isQualified ? (
                              <>
                                <CheckCircle2
                                  className="h-4 w-4 text-sky-600"
                                  aria-hidden="true"
                                />
                                <span className="sr-only">(Qualified)</span>
                              </>
                            ) : null}
                          </Link>
                          {onHold && (
                            <span className="inline-flex w-fit items-center rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-rose-700">
                              On hold
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-[15px] text-slate-700">{toLocationLabel(reseller) || "—"}</TableCell>
                      <TableCell>
                        {badge ? (
                          <span
                            className={[
                              "inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide",
                              badge.className,
                            ].join(" ")}
                          >
                            {badge.label}
                          </span>
                        ) : (
                          <span className="text-[15px] text-slate-500">—</span>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-center text-[15px] text-slate-700">
                        {formatInvoiceDate(reseller.LastInvoiceDate)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          variant="link"
                          onClick={() => setSelectedResellerForSales(reseller)}
                          className="h-auto p-0 text-[15px] font-semibold text-slate-900 hover:text-[color:var(--ops-accent)]"
                        >
                          <FormattedNumber value={salesLast30Days} {...CurrencyUtils.format} />
                        </Button>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-right text-[15px] font-medium text-slate-700">
                        <FormattedNumber value={salesLast3Months} {...CurrencyUtils.format} />
                      </TableCell>
                      <TableCell className="text-right">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="inline-flex items-center gap-2 rounded-xl"
                            >
                              Actions
                              <ChevronDown className="h-4 w-4" aria-hidden="true" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent
                            align="end"
                            className="w-auto rounded-xl border border-gray-100 bg-white p-0 shadow-sm"
                          >
                            <ResellerActions reseller={reseller} />
                          </PopoverContent>
                        </Popover>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        <div className="flex flex-col gap-3 border-t border-gray-100 px-4 pb-4 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[13px] text-slate-500">
            Showing {startIndex}–{endIndex} of {totalRows}
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Rows</span>
              <Select value={String(rowsPerPage)} onValueChange={(value) => setRowsPerPage(Number.parseInt(value, 10))}>
                <SelectTrigger className="h-10 w-[110px] rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {rowsPerPageOptions.map((option) => (
                    <SelectItem key={option} value={String(option)}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPage((current) => Math.max(0, current - 1))}
                disabled={safePage === 0}
                className="rounded-xl"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                Prev
              </Button>
              <p className="text-[13px] font-medium text-slate-700">
                Page {safePage + 1} of {totalPages}
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPage((current) => Math.min(totalPages - 1, current + 1))}
                disabled={safePage >= totalPages - 1}
                className="rounded-xl"
              >
                Next
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <Dialog
        open={Boolean(selectedResellerForSales)}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedResellerForSales(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Sales summary</DialogTitle>
          </DialogHeader>
          <SalesSummary
            salesData={selectedResellerForSales?.SalesData}
            fiscalYearMonthStart={fiscalYearMonthStart}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ResellersGrid;
